import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  HeartIcon,
  MapPinIcon,
  UserPlusIcon,
  FireIcon,
  BookmarkIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { collection, query, where, orderBy, limit, getDocs, doc, updateDoc, arrayUnion, arrayRemove, getDoc, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

// Helper function to convert Firestore timestamp to Date
const convertToDate = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'object' && timestamp !== null && 'seconds' in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp && typeof (timestamp as { toDate: () => Date }).toDate === 'function') {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  return new Date(String(timestamp));
};

interface DiscoverPostcard {
  id: string;
  imageURL: string;
  message: string;
  location: string;
  country: string;
  createdBy: string;
  createdAt: Date;
  isRead: boolean;
  isPublic: boolean;
  tags: string[];
  likes: number;
  translations: { [language: string]: string };
  template: {
    id: string;
    name: string;
    previewImage: string;
    borderStyle: string;
    colorFilter: string;
    fontFamily: string;
    textColor: string;
    backgroundColor?: string;
    animation: string;
  };
  emojiOverlays: unknown[];
  isAnonymous: boolean;
  authorName: string;
  authorPhotoURL?: string;
  isLiked: boolean;
  isBookmarked: boolean;
  likedByCount: number;
}

export function DiscoverPage() {
  const { currentUser } = useAuth();
  const [trendingPostcards, setTrendingPostcards] = useState<DiscoverPostcard[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<'trending' | 'nearby'>('trending');

  useEffect(() => {
    fetchDiscoverContent();
  }, [selectedCategory, currentUser]);

  const fetchDiscoverContent = async () => {
    try {
      setLoading(true);
      
      if (!currentUser) {
        // If no user is logged in, show limited content
        const publicPostcardsQuery = query(
          collection(db, 'postcards'),
          where('isPublic', '==', true),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        
        const querySnapshot = await getDocs(publicPostcardsQuery);
        const postcards: DiscoverPostcard[] = [];
        
        for (const docSnapshot of querySnapshot.docs) {
          const postcardData = docSnapshot.data();
          const userDoc = await getDoc(doc(db, 'users', postcardData.createdBy));
          const userData = userDoc.data();
          
          const createdAt = convertToDate(postcardData.createdAt);
          if (createdAt) {
            postcards.push({
              ...postcardData,
              id: docSnapshot.id,
              imageURL: postcardData.imageURL || '',
              message: postcardData.message || '',
              location: postcardData.location || '',
              country: postcardData.country || '',
              createdBy: postcardData.createdBy || '',
              createdAt,
              isRead: postcardData.isRead || false,
              isPublic: postcardData.isPublic || false,
              tags: postcardData.tags || [],
              likes: postcardData.likes || 0,
              translations: postcardData.translations || {},
              template: postcardData.template || {
                id: 'classic',
                name: 'Classic',
                previewImage: '/templates/classic-preview.jpg',
                borderStyle: 'classic',
                colorFilter: 'sepia(0.2)',
                fontFamily: 'serif',
                textColor: '#2c1810',
                backgroundColor: '#fef7e6',
                animation: 'fade'
              },
              emojiOverlays: postcardData.emojiOverlays || [],
              isAnonymous: postcardData.isAnonymous || false,
              authorName: userData?.displayName || 'Anonymous',
              authorPhotoURL: userData?.photoURL,
              isLiked: false,
              isBookmarked: false,
              likedByCount: postcardData.likes || 0
            });
          }
        }
        
        setTrendingPostcards(postcards);
        return;
      }

      // Fetch trending postcards (public postcards ordered by likes and recent activity)
      const trendingQuery = query(
        collection(db, 'postcards'),
        where('isPublic', '==', true),
        orderBy('likes', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(trendingQuery);
      const postcards: DiscoverPostcard[] = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const postcardData = docSnapshot.data();
        const userDoc = await getDoc(doc(db, 'users', postcardData.createdBy));
        const userData = userDoc.data();
        
        // Check if current user has liked this postcard
        const isLiked = postcardData.likedBy?.includes(currentUser.uid) || false;
        const isBookmarked = userData?.bookmarks?.includes(docSnapshot.id) || false;
        
        const createdAt = convertToDate(postcardData.createdAt);
        if (createdAt) {
          postcards.push({
            ...postcardData,
            id: docSnapshot.id,
            imageURL: postcardData.imageURL || '',
            message: postcardData.message || '',
            location: postcardData.location || '',
            country: postcardData.country || '',
            createdBy: postcardData.createdBy || '',
            createdAt,
            isRead: postcardData.isRead || false,
            isPublic: postcardData.isPublic || false,
            tags: postcardData.tags || [],
            likes: postcardData.likes || 0,
            translations: postcardData.translations || {},
            template: postcardData.template || {
              id: 'classic',
              name: 'Classic',
              previewImage: '/templates/classic-preview.jpg',
              borderStyle: 'classic',
              colorFilter: 'sepia(0.2)',
              fontFamily: 'serif',
              textColor: '#2c1810',
              backgroundColor: '#fef7e6',
              animation: 'fade'
            },
            emojiOverlays: postcardData.emojiOverlays || [],
            isAnonymous: postcardData.isAnonymous || false,
            authorName: userData?.displayName || 'Anonymous',
            authorPhotoURL: userData?.photoURL,
            isLiked,
            isBookmarked,
            likedByCount: postcardData.likes || 0
          });
        }
      }
      
      setTrendingPostcards(postcards);
      
    } catch (error) {
      console.error('Error fetching discover content:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (postcardId: string) => {
    if (!currentUser) return;
    
    try {
      // Create or update a like document in a separate likes collection
      const likeRef = doc(db, 'postcardLikes', `${postcardId}_${currentUser.uid}`);
      
      // Check if already liked
      const likeDoc = await getDoc(likeRef);
      const isLiked = likeDoc.exists();
      
      if (isLiked) {
        // Unlike: delete the like document
        await deleteDoc(likeRef);
        
        // Update local state
        setTrendingPostcards(prev => 
          prev.map(postcard => {
            if (postcard.id === postcardId) {
              return {
                ...postcard,
                isLiked: false,
                likedByCount: Math.max(0, postcard.likedByCount - 1)
              };
            }
            return postcard;
          })
        );
      } else {
        // Like: create the like document
        await setDoc(likeRef, {
          postcardId: postcardId,
          userId: currentUser.uid,
          createdAt: serverTimestamp()
        });
        
        // Update local state
        setTrendingPostcards(prev => 
          prev.map(postcard => {
            if (postcard.id === postcardId) {
              return {
                ...postcard,
                isLiked: true,
                likedByCount: postcard.likedByCount + 1
              };
            }
            return postcard;
          })
        );
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const toggleBookmark = async (postcardId: string) => {
    if (!currentUser) return;
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      const isBookmarked = userData.bookmarks?.includes(postcardId) || false;
      
      if (isBookmarked) {
        // Remove bookmark
        await updateDoc(userRef, {
          bookmarks: arrayRemove(postcardId)
        });
      } else {
        // Add bookmark
        await updateDoc(userRef, {
          bookmarks: arrayUnion(postcardId)
        });
      }
      
      // Update local state
      setTrendingPostcards(prev => 
        prev.map(postcard => 
          postcard.id === postcardId 
            ? { ...postcard, isBookmarked: !postcard.isBookmarked }
            : postcard
        )
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };



  const categories = [
    { id: 'trending', name: 'Trending', icon: FireIcon },
    { id: 'nearby', name: 'Nearby', icon: MapPinIcon }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold mb-2">Discover</h1>
          <p className="text-purple-100">
            Find amazing postcards from travelers around the world
          </p>
        </motion.div>
      </div>

      {/* Category Tabs */}
      <div className="card">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id as any)}
              className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
                selectedCategory === category.id
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <category.icon className="h-4 w-4 mr-2" />
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Trending/Nearby Postcards Grid */}
      {(selectedCategory === 'trending' || selectedCategory === 'nearby') && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            {selectedCategory === 'trending' ? (
              <>
                <FireIcon className="h-5 w-5 mr-2 text-orange-600" />
                Trending Postcards
              </>
            ) : (
              <>
                <MapPinIcon className="h-5 w-5 mr-2 text-green-600" />
                Nearby Postcards
              </>
            )}
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingPostcards.map((postcard, index) => (
                <motion.div
                  key={postcard.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="relative">
                    <div className="w-full" style={{ aspectRatio: '4 / 5' }}>
                      <img
                        src={postcard.imageURL}
                        alt="Postcard"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute top-2 right-2 flex space-x-2">
                      <button
                        onClick={() => toggleLike(postcard.id)}
                        className="bg-white bg-opacity-90 p-2 rounded-full shadow-md hover:bg-opacity-100 transition-all"
                      >
                        {postcard.isLiked ? (
                          <HeartIconSolid className="h-4 w-4 text-red-500" />
                        ) : (
                          <HeartIcon className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                      <button
                        onClick={() => toggleBookmark(postcard.id)}
                        className="bg-white bg-opacity-90 p-2 rounded-full shadow-md hover:bg-opacity-100 transition-all"
                      >
                        {postcard.isBookmarked ? (
                          <BookmarkIconSolid className="h-4 w-4 text-blue-500" />
                        ) : (
                          <BookmarkIcon className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <img
                        src={postcard.authorPhotoURL || '/default-avatar.png'}
                        alt={postcard.authorName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm">{postcard.authorName}</h3>
                        <p className="text-xs text-gray-600 flex items-center">
                          <MapPinIcon className="h-3 w-3 mr-1" />
                          {postcard.location}, {postcard.country}
                        </p>
                      </div>
                      <button className="text-purple-600 hover:text-purple-700 text-xs">
                        <UserPlusIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <p className="text-gray-900 text-sm mb-3 line-clamp-3">
                      {postcard.message}
                    </p>

                    <div className="flex flex-wrap gap-1 mb-3">
                      {postcard.tags.slice(0, 2).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                        >
                          #{tag}
                        </span>
                      ))}
                      {postcard.tags.length > 2 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          +{postcard.tags.length - 2}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span className="flex items-center">
                        <HeartIcon className="h-3 w-3 mr-1" />
                        {postcard.likedByCount}
                      </span>
                      <span>{convertToDate(postcard.createdAt)?.toLocaleDateString()}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 